import {
    Box,
    InputAdornment,
    Modal,
    Stack,
} from "@mui/material";
import { useForm } from "@refinedev/react-hook-form";
import { useList, useModal, useSelect, useTranslate, useGo, useCreate } from "@refinedev/core";
import { useLocation } from "react-router";
import { CreateOrEdit } from "@/components/editcreate";
import { TextFieldElement } from "react-hook-form-mui";
import { RHFAutocomplete } from "@/components/form/RHFAutocomplete";
import { zodResolver } from "@hookform/resolvers/zod";
import { editProductSchema, createProductSchema, FormValuesType, ICollection } from "@/utils/zod/productCollectionFormValidation";
import { AddMediaForm } from '@/components/form/AddMediaForm';
import RHFAutoCompleteString from "@/components/form/RHFAutocompleteString";
import { IDeal } from "@/utils/zod/dealFormValidation";
import { RestDataProvider } from "@/utils/data-provider/provider";
import { API_URL as apiUrl } from "@/utils";
import { Star } from "@mui/icons-material";
import { CollectionAddEditForm } from "../collections";
import { uomsData } from "@/utils/data-provider/data";
import { UploadMediaButton } from "@/components/form/UploadMediaButton";
import { uploadMultipleMedia } from "@/utils/uploadMedia";
import { UploadResponse } from "@/utils/uploadToCdn";
import { useState } from "react";

interface Props {
    actionType: "create" | "edit";
}

export const ProductAddEditForm = ({ actionType }: Props) => {
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const go = useGo();
    const { search } = useLocation();

    const { data: dealOptions } = useList<IDeal>({
        resource: "deals",
    });

    const formValues = useForm<FormValuesType<typeof actionType, "products">>({
        refineCoreProps: {
            resource: "products",
            redirect: false,
            onMutationSuccess: () => {
                // Use the search parameters from the URL
                const queryParams = Object.fromEntries(new URLSearchParams(search));
                go({
                    to: "/products",
                    type: "push",
                    query: queryParams
                });
            },
            ...(actionType === "edit" && {
                queryOptions: {
                    select: (data: any) => {
                        const collections = data.data.collections?.map((collection: { id: number }) => collection.id) || [];
                        const transformedData = ({
                            ...data.data,
                            collections: collections,
                            features: data.data.features?.join(", ") || "",
                            material: data.data.material?.charAt(0).toUpperCase() + data.data.material?.slice(1).toLowerCase() || "",
                        });
                        return { data: transformedData };
                    }
                },
            }),
        },
        resolver: zodResolver(
            actionType === "create" ? createProductSchema : editProductSchema
        ),
        ...(actionType === "create" && {
            defaultValues: {
                name: "",
                sku_id: "",
                description: null,
                features: "",
                material: "",
                available_quantity: 0,
                minimum_order_quantity: 1,
                uom: "pcs",
                cost_price: 0,
                tags: [],
                medias: [],
                pdf_catalog_media_id: null,
                collections: [],
                status: "active",
                deal_name: "",
                rating_stars: Number((Math.random() * 0.5 + 4.5).toFixed(1)), // Random rating between 4.5 and 5
                review_count: Math.floor(Math.random() * 101) + 550, // Random review count between 550 and 650
            } as FormValuesType<typeof actionType, "products">,
        }),
    });

    // Set deal_name if it's available and form is in create mode
    if (actionType === "create" && dealOptions?.data?.length && !formValues.getValues("deal_name")) {
        formValues.setValue("deal_name", dealOptions.data[0].name);
    }

    const { register, handleSubmit, refineCore: { onFinish }, formState: { dirtyFields } } = formValues;
    const t = useTranslate();
    register("pdf_catalog_media_id");
    if (actionType === "edit") {
        register("id");
    }

    const { options: CollectionOptions } = useSelect({
        resource: "collections",
        optionLabel: "name",
        optionValue: "id",
    });

    const { mutateAsync: createMutate } = useCreate();

    const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || []);
        setIsUploading(true);
        try {
          const uploadResponse: UploadResponse[] = await uploadMultipleMedia(files, (progress) => {
            setUploadProgress(progress);
          });
          const uploadResponseData = await createMutate({
            resource: "medias",
            values: {
                "filename": uploadResponse[0].file_name,
                "resource_type": uploadResponse[0].resource_type,
                "cdn_url": uploadResponse[0].cdn_url,
                "size_in_kb": uploadResponse[0].size_in_kb,
                "title": uploadResponse[0].file_name,
                "type": uploadResponse[0].type, 
              }
          });
          formValues.setValue("pdf_catalog_media_id", uploadResponseData.data.id, {shouldDirty: true});
          
        } catch (error) {
          console.error('Upload failed:', error);
        } finally {
          setIsUploading(false);
        }
      };

    const onSubmit = handleSubmit((values) => {

        const finishValues = {
            ...values,
            pdf_catalog_media_id: values.pdf_catalog_media_id,
            collections: values.collections.map((id: number) => ({ id })),
            features: values.features?.split(", "),
            material: values.material?.charAt(0).toUpperCase() + values.material?.slice(1).toLowerCase(),
        }

        onFinish(finishValues);
    });

    const { visible: newCollectionCreateVisible, show: newCollectionCreateShow, close: newCollectionCreateClose } = useModal();

    return (
        <CreateOrEdit actionType={actionType} formValues={formValues} handleSubmit={onSubmit}>
            <Box sx={{ p: 3 }}>
                <Box component="form" sx={{ display: "flex", flexDirection: "column", gap: 2 }} autoComplete="off">

                    <AddMediaForm name="medias" />
                    <UploadMediaButton type="hyperlink" onInputChange={handleUpload} allowMultiple={false} uploadProgress={uploadProgress} isUploading={isUploading}/>

                    <Stack direction="row" gap={2}>
                        <TextFieldElement
                            name="name"
                            label={t("products.fields.name")}
                            fullWidth
                            autoFocus
                            required={true}
                            slotProps={{
                                inputLabel: {
                                    sx: {
                                        '& .MuiFormLabel-asterisk': {
                                            color: 'error.main',
                                            fontSize: '24px',
                                        },
                                    },
                                },
                            }}
                            size="small"
                        />
                        <TextFieldElement
                            name="sku_id"
                            label={t("products.fields.sku_id")}
                            fullWidth
                            autoFocus
                            required={true}
                            slotProps={{
                                inputLabel: {
                                    sx: {
                                        '& .MuiFormLabel-asterisk': {
                                            color: 'error.main',
                                            fontSize: '24px',
                                        },
                                    },
                                },
                            }}
                            size="small"
                        />

                    </Stack>

                    <RHFAutocomplete
                        name={"collections"}
                        label={t("products.fields.collection")}
                        options={CollectionOptions}
                        multiple={true}
                        required={true}
                        onValueChange={(value) => {
                            if (value?.length) {
                                const lastValue = value[value.length - 1];
                                RestDataProvider(apiUrl).getOne<ICollection>({
                                    resource: "collections",
                                    id: lastValue,
                                }).then(({ data }) => {
                                    !formValues.getValues("description") && !dirtyFields.description && formValues.setValue("description", data.description || "");
                                    !formValues.getValues("cost_price") && !dirtyFields.cost_price && formValues.setValue("cost_price", data.cost_price || 0);
                                    !formValues.getValues("minimum_order_quantity") && !dirtyFields.minimum_order_quantity && formValues.setValue("minimum_order_quantity", data.minimum_order_quantity || 1);
                                    !formValues.getValues("uom") && !dirtyFields.uom && formValues.setValue("uom", data.uom || "pcs");
                                    !formValues.getValues("deal_name") && !dirtyFields.deal_name && formValues.setValue("deal_name", data.deal_name || "");
                                });
                            }
                        }}
                        onCreateNew={() => {
                            newCollectionCreateShow();
                        }}
                        size="small"
                    />

                    <TextFieldElement
                        name="description"
                        label={t("products.fields.description")}
                        multiline
                        fullWidth
                        size="small"
                        sx={{ flex: 1 }}
                    />

                    <Stack direction="row" gap={2}>
                        <TextFieldElement
                            label={t("products.fields.price")}
                            name={'cost_price'}
                            type={'number'}
                            slotProps={{
                                input: {
                                    startAdornment: <InputAdornment position="start">₹</InputAdornment>
                                },
                                inputLabel: {
                                    sx: {
                                        '& .MuiFormLabel-asterisk': { color: 'error.main', fontSize: '24px' },
                                    },
                                },
                            }}
                            size="small"
                            sx={{ flex: 1 }}
                            required={true}
                        />

                        <TextFieldElement
                            name="available_quantity"
                            label={t("products.fields.available_quantity")}
                            type="number"
                            size="small"
                        />

                        <TextFieldElement
                            name="minimum_order_quantity"
                            label={t("products.fields.moq")}
                            type="number"
                            size="small"
                            sx={{ flex: 1 }}
                            required={true}
                            slotProps={{
                                inputLabel: {
                                    sx: {
                                        '& .MuiFormLabel-asterisk': { color: 'error.main', fontSize: '24px' },
                                    },
                                },
                            }}
                        />

                        <RHFAutoCompleteString
                            name="uom"
                            label={t("products.fields.uom")}
                            size="small"
                            options={uomsData}
                            sx={{ flex: 1 }}
                            createNew={true}
                        />
                    </Stack>

                    <Stack direction="row" gap={2}>
                        <TextFieldElement
                            name="features"
                            label={t("products.fields.features")}
                            fullWidth
                            placeholder="Feature 1, Feature 2, Feature 3"
                            helperText="seperated by comma symbol"
                            size="small"
                        />

                        <TextFieldElement
                            name="material"
                            label={t("products.fields.material")}
                            fullWidth
                            size="small"
                            required={true}
                            slotProps={{
                                inputLabel: {
                                    sx: {
                                        '& .MuiFormLabel-asterisk': { color: 'error.main', fontSize: '24px' },
                                    },
                                },
                            }}
                        />
                    </Stack>

                    <Stack direction="row" gap={2}>
                        <TextFieldElement
                            name="rating_stars"
                            type="number"
                            defaultValue={Math.random() * 0.5 + 4.5}
                            label={t("products.fields.rating_stars")}
                            fullWidth
                            slotProps={{
                                input: {
                                    startAdornment: <InputAdornment position="start"><Star sx={{ color: "warning.main", fontSize: "16px" }} /></InputAdornment>
                                }
                            }}
                            size="small"
                        />

                        <TextFieldElement
                            name="review_count"
                            type="number"
                            defaultValue={Math.floor(Math.random() * 101) + 550}
                            label={t("products.fields.review_count")}
                            fullWidth
                            size="small"
                        />
                    </Stack>

                    <Stack direction="row" gap={2}>
                        <RHFAutoCompleteString
                            name="deal_name"
                            label={t("products.fields.deal_name")}
                            options={dealOptions?.data?.map((deal) => deal.name) || []}
                            sx={{ flex: 1 }}
                            required={true}
                            size="small"
                            defaultValue={dealOptions?.data?.[0]?.name}
                        />
                        <RHFAutoCompleteString
                            name="status"
                            label={t("products.fields.status.label")}
                            options={["active", "draft", "archived"]}
                            sx={{ flex: 1 }}
                            size="small"
                        />
                    </Stack>
                </Box>
            </Box>
            <Modal
                open={newCollectionCreateVisible}
                onClose={newCollectionCreateClose}
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 700,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 1,
                    overflow: 'auto',
                    maxHeight: '90vh',
                }}>
                    <CollectionAddEditForm actionType="create" redirect={false} onSave={(finishValues: FormValuesType<"edit", "collections">) => {
                        newCollectionCreateClose();
                        CollectionOptions.push({
                            label: finishValues.name,
                            value: finishValues.id
                        });
                        formValues.setValue("collections", [...formValues.getValues("collections"), finishValues.id]);
                        formValues.setValue("description", finishValues.description);
                        formValues.setValue("cost_price", finishValues.cost_price);
                        formValues.setValue("minimum_order_quantity", finishValues.minimum_order_quantity);
                        formValues.setValue("uom", finishValues.uom);
                    }}/>
                </Box>
            </Modal>
        </CreateOrEdit>
    );
};
